import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, useAgentsApi } from '../apis/agents-apis';
import { Agent } from '../types';
import { useToast } from "../components/ui/Toast";
import AgentForm, { AgentFormRef } from './AgentForm';
import { useNavigation } from '../contexts/NavigationContext';
import { NavigationWarningDialog } from '../components/NavigationWarningDialog';
import { Spinner } from "../components/ui/Spinner";

export const EditAgentPage: React.FC = () => {
  const formRef = useRef<AgentFormRef>(null);
  const navigate = useNavigate();
  const { id } = useParams();
  const { addToast } = useToast();
  const initialFetchMade = useRef(false);
  
  const [agent, setAgent] = useState<Agent | null>(null);
  const [originalAgent, setOriginalAgent] = useState<Agent | null>(null);
  const [isReadOnly, setIsReadOnly] = useState(true);
  const [existingAgents, setExistingAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const agentsApi = useAgentsApi();
  
  const { 
    showNavigationWarning, 
    pendingNavigation, 
    setShowNavigationWarning, 
    setPendingNavigation 
  } = useNavigation();

  const hasUnsavedChanges = !isReadOnly && 
    JSON.stringify(agent) !== JSON.stringify(originalAgent);

  // Handle browser back button
  const handleBeforeUnload = useCallback((event: BeforeUnloadEvent) => {
    if (hasUnsavedChanges) {
      event.preventDefault();
      event.returnValue = '';
    }
  }, [hasUnsavedChanges]);

  // Set up block for browser back button
  useEffect(() => {
    // Add extra history entry when editing starts
    if (hasUnsavedChanges) {
      window.history.pushState(null, '', window.location.pathname);
    }

    const handlePopState = (event: PopStateEvent) => {
      if (hasUnsavedChanges) {
        // Push state back to prevent navigation
        window.history.pushState(null, '', window.location.pathname);
        // Show warning dialog
        setShowNavigationWarning(true);
        setPendingNavigation('/marketplace/agents');
      }
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges, handleBeforeUnload, setShowNavigationWarning, setPendingNavigation]);

  useEffect(() => {
    const fetchData = async () => {
      if (id && !initialFetchMade.current) {
        initialFetchMade.current = true;
        try {
          const getAllAgents = true;
          const [agentData, allAgents] = await Promise.all([
            agentsApi.getAgent(id),
            agentsApi.getAgents(getAllAgents)
          ]);
          setAgent(agentData);
          setOriginalAgent(agentData);
          setExistingAgents(allAgents);
        } catch (error: any) {
          console.error('Error fetching data:', error);
          
          if (error instanceof ApiError && error.status === 403) {
            navigate('/marketplace/unauthorized');
            return;
          }
          
          // For all other errors, show the specific error message or fallback
          const errorMessage = error instanceof ApiError 
            ? error.message 
            : "Failed to fetch agent details";
            
          addToast({
            title: "Error",
            description: errorMessage,
            variant: "destructive",
            duration: 5000
          });
          navigate('/marketplace/agents');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [id, navigate, addToast]);

  const handleSubmit = async () => {
    if (!agent) return;
  
    const isValid = await formRef.current?.validateForm() ?? false;
    if (!isValid) {
      addToast({
        title: "Validation Error",
        description: "Please fill in all required fields correctly",
        variant: "destructive",
        duration: 4000
      });
      return;
    }
  
    try {
      setIsSaving(true);
      await agentsApi.updateAgent(agent);
      const savedAgent = { ...agent };
      setOriginalAgent(savedAgent);
      setAgent(savedAgent);
      setIsReadOnly(true);
      
      addToast({
        title: "Success",
        description: "Agent " + agent.agentName + " has been successfully updated",
        variant: "default",
        duration: 4000
      });
  
      if (pendingNavigation) {
        navigate(pendingNavigation);
        setPendingNavigation(null);
      }
    } catch (error) {
      console.error('Error updating agent:', error);
      addToast({
        title: "Error",
        description: "Failed to update agent: " + String(error),
        variant: "destructive",
        duration: 8000
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscardChanges = () => {
    if (hasUnsavedChanges) {
      setShowNavigationWarning(true);
      setPendingNavigation(null);
    } else {
      setAgent(originalAgent);
      setIsReadOnly(true);
    }
  };

  const handleConfirmNavigation = () => {
    setShowNavigationWarning(false);
    setAgent(originalAgent);
    setIsReadOnly(true);
    
    if (pendingNavigation) {
      // Go back twice to handle the extra history entry we added
      window.history.go(-2);
      setPendingNavigation(null);
    }
  };

  const handleCancelNavigation = () => {
    setShowNavigationWarning(false);
    setPendingNavigation(null);
  };

  const handleNavigateBack = () => {
    if (hasUnsavedChanges) {
      setShowNavigationWarning(true);
      setPendingNavigation('/marketplace/agents');
    } else {
      navigate('/marketplace/agents');
    }
  };

  if (loading || !agent) {
    return (
      <div className="flex flex-col items-center my-8 gap-2">
        <Spinner className="border-blue-500" />
        <p className="text-center text-muted-foreground">Loading Agent...</p>
      </div>
    );
  }

  return (
    <>
      <AgentForm
        ref={formRef} 
        agent={agent}
        originalAgent={originalAgent}
        onAgentChange={setAgent}
        onSubmit={handleSubmit}
        onCancel={handleNavigateBack}
        isEditing={true}
        readOnly={isReadOnly}
        onEditClick={() => setIsReadOnly(false)}
        onDiscardChanges={handleDiscardChanges}
        hasUnsavedChanges={hasUnsavedChanges}
        setIsReadOnly={setIsReadOnly}
        existingAgents={existingAgents}
        isSaving={isSaving}
      />

      <NavigationWarningDialog
        open={showNavigationWarning}
        onConfirm={handleConfirmNavigation}
        onCancel={handleCancelNavigation}
        title="Discard Changes?"
        description="You have unsaved changes that will be lost if you leave this page. Are you sure you want to discard them?"
      />
    </>
  );
};

export default EditAgentPage;